import React, { useState } from 'react';
import axios from 'axios';
import { MailWarning, X, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../config';

// Persistent banner that nudges users to verify their email address.
// • Hidden for: verified users, Google-auth users (auto-verified), admins
// • Dismissible PER SESSION ONLY (sessionStorage) so it comes back on every
//   fresh app start until verification actually happens.
// • "Reinvia" button hits POST /api/auth/resend-verification (already exists).
const EmailVerifyBanner = () => {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('rv_verify_banner_dismissed') === '1'; }
    catch { return false; }
  });

  // Visibility guards
  if (!user) return null;
  if (user.is_verified) return null;
  if (user.auth_provider === 'google') return null;
  if (dismissed) return null;

  const handleResend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(r.data?.message || 'Email di verifica reinviata. Controlla la posta.', {
        duration: 5000,
        icon: <CheckCircle2 className="w-5 h-5 text-green-400" />,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Impossibile inviare l\'email di verifica';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem('rv_verify_banner_dismissed', '1'); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="mx-4 mt-3 mb-1 px-3 py-2.5 rounded-2xl bg-gradient-to-r from-amber-500/15 to-orange-500/10 border border-amber-500/30 flex items-center gap-3"
      data-testid="email-verify-banner"
    >
      <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex-shrink-0 flex items-center justify-center">
        <MailWarning className="w-5 h-5 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-tight">Verifica la tua email</p>
        <p className="text-[11px] text-amber-200/80 leading-snug truncate">
          {user.email ? `Conferma ${user.email}` : 'Conferma il tuo indirizzo'} per accedere a tutte le funzioni.
        </p>
      </div>
      <button
        onClick={handleResend}
        disabled={sending}
        className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-black text-xs font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="email-verify-resend-btn"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reinvia'}
      </button>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-amber-200/60 hover:text-white hover:bg-white/5 transition-colors"
        aria-label="Chiudi"
        data-testid="email-verify-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EmailVerifyBanner;
